import { useEffect, useRef, useState } from "react";
import {
  useGetPaginatedConversationsQuery,
  useLazyGetConversationMessagesQuery,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { PageSpinner } from "@financial-advisory/ui/components/spinners";
import {
  ChatInteractionsMetricCard,
  ChatCompletionMetricCard,
} from "@targeted-support/ui/features/overview";
import { Copy, Search } from "lucide-react";
import { type PreviousConversation } from "./TestPage2";

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 400;

export const PastConversationsPage = ({
  navigateToTest,
}: {
  navigateToTest: (previousConversation: PreviousConversation) => void;
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, isLoading, isFetching, error } =
    useGetPaginatedConversationsQuery(
      {
        page: currentPage,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
      },
      { refetchOnMountOrArgChange: true },
    );

  const [fetchConversationMessages] = useLazyGetConversationMessagesQuery();

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const totalPages = data?.total_pages ?? 1;
  const conversations = data?.content ?? [];

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handleRowClick = async (sessionId: string) => {
    const { data } = await fetchConversationMessages(sessionId);
    if (!data) return;

    navigateToTest({
      sessionId,
      sessionVersionId: data.session_version_id,
      messages: data.messages ?? [],
      isComplete: data.is_complete ?? false,
      shouldTerminateEarly: data.should_terminate_early ?? false,
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageSpinner size={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6 text-brand-foreground">
          Past Conversations
        </h1>
        <p className="text-red-600">
          Failed to load conversations. Please try again.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 text-brand-foreground">
        Past Conversations
      </h1>

      <div className="flex gap-4 mb-6">
        <ChatInteractionsMetricCard />
        <ChatCompletionMetricCard />
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search by session ID..."
          className="w-full pl-10 pr-4 py-2 text-sm border border-brand-border rounded-md bg-brand-card text-brand-foreground placeholder:text-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
        />
      </div>

      {conversations.length === 0 ? (
        <p className="text-brand-muted">No past conversations found.</p>
      ) : (
        <div className="w-full">
            <div className="overflow-x-auto max-h-[50vh] overflow-y-auto border border-brand-border rounded-lg shadow relative">
            {isFetching && (
              <div className="absolute inset-0 bg-brand-card/50 z-20 flex items-center justify-center">
                <PageSpinner size={4} />
              </div>
            )}
            <table className="min-w-full bg-brand-card">
              <thead className="sticky top-0 z-10">
                <tr className="relative">
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">Session ID</span>
                  </th>
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">Started</span>
                  </th>
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">Status</span>
                  </th>
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">Messages</span>
                  </th>
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">May be raised</span>
                  </th>
                  <th className="relative px-6 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
                    <div className="absolute inset-0 bg-brand-primary opacity-10" />
                    <span className="relative">Feedback</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {conversations.map((conv) => (
                  <tr
                    key={conv.session_id}
                    onClick={() => handleRowClick(conv.session_id)}
                    className="hover:bg-brand-primary/5 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-foreground">
                      {conv.session_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                      {formatDate(conv.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                      <span
                        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                          conv.is_complete
                            ? "bg-green-200 text-green-800"
                            : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {conv.is_complete ? "COMPLETE" : "IN PROGRESS"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                      {conv.message_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                      {conv.resolved_suggestions.join(", ")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-brand-muted">
                      <div className="flex items-center gap-2">
                        <span
                          title={
                            conv.feedback.length > 0
                              ? conv.feedback.join(", ")
                              : undefined
                          }
                          className="cursor-default"
                        >
                          {conv.feedback.length > 0
                            ? conv.feedback.join(", ").slice(0, 10) + "..."
                            : ""}
                        </span>
                        {conv.feedback.length > 0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                conv.feedback.join(", ")
                              );
                            }}
                            className="p-1 text-brand-muted hover:text-brand-foreground rounded hover:bg-brand-primary/10 transition-colors"
                            title="Copy feedback"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-brand-foreground bg-brand-card border border-brand-border rounded-md hover:bg-brand-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-brand-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-brand-foreground bg-brand-card border border-brand-border rounded-md hover:bg-brand-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNum = i + 1;
                  if (
                    pageNum <= 3 ||
                    (pageNum >= currentPage - 1 &&
                      pageNum <= currentPage + 1) ||
                    pageNum > totalPages - 3
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1 text-sm rounded-md ${
                          currentPage === pageNum
                            ? "bg-brand-primary text-white"
                            : "bg-brand-card text-brand-foreground border border-brand-border hover:bg-brand-primary/5"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    pageNum === currentPage - 2 ||
                    pageNum === currentPage + 2
                  ) {
                    return (
                      <span key={pageNum} className="px-2 text-brand-muted">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
